import React, { useEffect, useState } from 'react';
import { HeadlineLarge, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useDispatch, useSelector } from 'react-redux';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { SpaceBetweenRow } from "./StyleComponents";
import Colours from '@shared/ui/atoms/Colours';
import CalendarSwitch from '@shared/ui/molecules/CalendarSwitch';
import { redirectTo, selectCurrentUser, updateCurrentUser } from "@shared/store/auth.duck";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { Switch } from '@shared/ui/atoms/Switch';
import { updateWhereaboutsRemindersRequest } from '@features/settings/services/UserService';
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { UsersFavouriteOfficesAndDesksList } from "./personal-preferences-page/UsersFavouriteOfficesAndDesksList";
import { Trans, useTranslation } from "react-i18next";
import { UsersDefaultOffice } from "./personal-preferences-page/UsersDefaultOffice";
import { UsersCarParkingDetails } from "./personal-preferences-page/UsersCarParkingDetails";
import { deepLinkTo, isInTeams } from "@shared/utils/TeamsUtils";
import { teamsTabs, uiUrl } from "@shared/services/EnvironmentVariables";
import { BirthdaySection } from "./components/BirthdaySection";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import i18next from "i18next";
import moment from "moment";
import { WithDateLocale } from "@shared/hooks/WithDateLocale";
import { useLocation } from "react-router";

export default function PersonalPreferencesPage() {
  const dispatch = useDispatch();
  const current = useSelector(selectCurrentUser);
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const [marketingEmailsEnabled, setMarketingEmailsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();
  const {updateLocale} = WithDateLocale();
  const location = useLocation();

  useEffect(() => {
    setEmailSubscribed(!!current?.allowWhereaboutsReminders);
    setMarketingEmailsEnabled(!!current?.allowMarketingEmails);
  }, [current]);

  const changeLanguage = (lng: string) => {
    moment.locale(lng);
    updateLocale();
    i18next.changeLanguage(lng);
  }

  const updateWhereaboutsReminders = async (allowWhereaboutsReminders: boolean, allowMarketingEmails: boolean) => {
    setLoading(true);
    try {
      await updateWhereaboutsRemindersRequest(allowWhereaboutsReminders, allowMarketingEmails);
      dispatch(successNotification('successfully updated whereabouts reminder'));
      dispatch(updateCurrentUser(true));
      setEmailSubscribed(allowWhereaboutsReminders);
      setMarketingEmailsEnabled(allowMarketingEmails);
    } catch {
      dispatch(failureNotification('Failed to update whereabouts reminder'));
    }
    setLoading(false);
  }

  const onBackClick = () => {
    // When opened from the Whereabouts tab (/whereabouts/preferences) go back there,
    // rather than deep-linking across to the Management tab.
    if (location.pathname.startsWith('/whereabouts')) {
      dispatch(redirectTo('/whereabouts/team'));
      return;
    }
    if (isInTeams()) {
      const externalUrl = `${uiUrl}/manage`
      deepLinkTo(teamsTabs.settings, externalUrl);
    } else {
      dispatch(redirectTo('/manage'))
    }
  }

  return (
    <>
      {loading && <LoadingSpinner fullScreen={true}/>}
      <GoBackButton onClick={onBackClick}/>

      <HeadlineLarge style={{marginBottom: 24}}>{t('settings.personal-pref')}</HeadlineLarge>
      <BodyRegular style={{marginBottom: 32}}>{t('settings.personal-pref-desc')}</BodyRegular>

      <HeadlineSmall style={{marginBottom: 24}}>{t('personal-perf.office-perf')}</HeadlineSmall>

      <UsersFavouriteOfficesAndDesksList />
      <UsersDefaultOffice />
      <UsersCarParkingDetails />

      <HeadlineSmall style={{marginBottom: 24}}>{t('personal-perf.other-perf')}</HeadlineSmall>

      <BirthdaySection userId={current?.id}
                       birthdayDay={current?.birthdayDay}
                       birthdayMonth={current?.birthdayMonth} />

      <div style={{marginTop: 24, marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${Colours.mildGrey}`}}>
        <Icon icon={IconTypes.Calendar} size="mediumlarge" style={{marginBottom: 8}}/>
        <SpaceBetweenRow style={{marginBottom: 12}}>
          <BodyLarge colour={Colours.blue}>{t('settings.email-reminder')}</BodyLarge>
          <Switch value={emailSubscribed}
                  onChange={(val: boolean) => updateWhereaboutsReminders(val, marketingEmailsEnabled)} label=""/>
        </SpaceBetweenRow>
        <BodyRegular>{t('settings.email-reminder-desc')}</BodyRegular>
      </div>

      <div style={{marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${Colours.mildGrey}`}}>
        <Icon icon={IconTypes.Calendar} size="mediumlarge" style={{marginBottom: 8}}/>
        <SpaceBetweenRow style={{marginBottom: 12}}>
          <BodyLarge colour={Colours.blue}>{t('settings.calendar-link')}</BodyLarge>
          <CalendarSwitch/>
        </SpaceBetweenRow>
        <FlatContentCard style={{padding: 24, marginBottom: 24}}>
          <BodyRegular>{t('settings.calendar-link-tip')}</BodyRegular>
        </FlatContentCard>
        <BodyRegular><Trans i18nKey={'settings.calendar-link-desc'} /></BodyRegular>
      </div>

      <div style={{marginBottom: 16, borderBottom: `1px solid ${Colours.mildGrey}`}}>
        <Icon icon={IconTypes.Email} size="mediumlarge"/>
        <SpaceBetweenRow style={{marginBottom: 12}}>
          <BodyLarge colour={Colours.blue}>{t('settings.marketing-emails')}</BodyLarge>
          <Switch value={marketingEmailsEnabled}
                  onChange={(val: boolean) => updateWhereaboutsReminders(emailSubscribed, val)} label=""/>
        </SpaceBetweenRow>
        <BodyRegular>{t('settings.marketing-emails-desc')}</BodyRegular>
      </div>

      <div>
        <Icon icon={IconTypes.Flag} size="mediumlarge" style={{marginBottom: 8}}/>
        <BodyLarge colour={Colours.blue} style={{marginBottom: 12}}>{t('settings.languages')}</BodyLarge>
        <OutlineButton text={"languages.english"} click={() => changeLanguage('en')} style={{marginBottom: 8}} />
        <OutlineButton text={"languages.french"} click={() => changeLanguage('fr')} style={{marginBottom: 8}} />
        <OutlineButton text={"languages.german"} click={() => changeLanguage('de')} style={{marginBottom: 8}} />
        <OutlineButton text={"languages.spanish"} click={() => changeLanguage('es')} />
      </div>

    </>
  );
}

